import {
  EVENT_METRICS,
  ORG_ROLE_VALUES,
  SLA_STATE_VALUES,
  STATUS_CATEGORY_VALUES,
  TICKET_CHANNEL_VALUES,
  TICKET_ORIGIN_VALUES,
  TICKET_PRIORITY_VALUES,
  TICKET_SENTIMENT_VALUES,
  TYPE_CATEGORY_VALUES,
  VERIFIED_VIA_VALUES,
  WORKFLOW_TRIGGER_GROUPS,
  type ConditionFieldKind,
  type WorkflowTrigger,
} from "@sagot/constants";
import type { ServiceFieldObject, ServiceFieldOption } from "./service-refs";

/**
 * The Service Desk condition facts (scope 14.2): which triggers know them, the values of the ones with a short list, and the
 * custom fields of the tickets (`ticket.field.<key>`). The names and kinds themselves are `CONDITION_FIELDS` in `@sagot/constants`.
 */

/** The ticket moments a run has a ticket for (ST1-ST17); the client-started intake has none yet. */
export const TICKET_TRIGGERS: ReadonlySet<string> = new Set(WORKFLOW_TRIGGER_GROUPS.service.filter((trigger) => trigger !== "ticket_intake_requested"));
export const isTicketTrigger = (trigger: string | undefined): boolean => trigger !== undefined && TICKET_TRIGGERS.has(trigger);

/** The groups of facts a ticket moment can read, in the order the picker lists them (a conversation, a visitor or a lead is not there). */
export const TICKET_FACT_GROUPS = ["ticket", "organisation", "contact", "event", "time"] as const;

export const TICKET_FIELD_PREFIX = "ticket.field.";
export const ORGANISATION_ATTRIBUTE_PREFIX = "organisation.attr.";
export const CONTACT_ATTRIBUTE_PREFIX = "contact.attr.";

const FACT_PREFIX: Readonly<Record<ServiceFieldObject, string>> = {
  ticket: TICKET_FIELD_PREFIX,
  organisation: ORGANISATION_ATTRIBUTE_PREFIX,
  contact: CONTACT_ATTRIBUTE_PREFIX,
};

/** The condition fact of a custom field or attribute (`ticket.field.serial`, `organisation.attr.region`, `contact.attr.badge`). */
export const factNameOf = (item: ServiceFieldOption): string => `${FACT_PREFIX[item.object ?? "ticket"]}${item.key}`;

/** The picker group a custom field or attribute is listed in: the ticket's, the organisation's or the contact's. */
export const factGroupOf = (item: ServiceFieldOption): ServiceFieldObject => item.object ?? "ticket";
const SERVICE_NAMESPACES: ReadonlySet<string> = new Set(["ticket", "organisation", "contact"]);

/** The event facts only some moments carry (the payload of each trigger, `contracts/service-desk/workflow-triggers.json`). */
const EVENT_FACT_TRIGGERS: Readonly<Record<string, readonly string[]>> = {
  "event.from": ["ticket_state_changed", "order_status_changed", "quote_status_changed"],
  "event.to": ["ticket_state_changed", "order_status_changed", "quote_status_changed"],
  "event.outcome": ["order_approval_decided"],
  "event.score": ["ticket_csat_received"],
  "event.low": ["ticket_csat_received"],
  "event.metric": ["ticket_sla_warning", "ticket_sla_breached"],
  "event.n": ["ticket_unassigned_due", "ticket_waiting_reminder_due", "ticket_pending_expired", "ticket_auto_close_due"],
  "event.mentions": ["ticket_note_added"],
};
/** Facts of the engine's own that a ticket moment carries only sometimes (`reason`: why a ticket changed or went back to the queue); `event.name` is a visitor's tracked event, never a ticket's. */
const TICKET_EVENT_FACTS: Readonly<Record<string, readonly string[]>> = {
  "event.reason": ["ticket_updated", "ticket_returned_to_queue"],
  "event.name": [],
};

/** Event facts of the visits phase: no moment of this release carries them. */
const LATER_EVENT_FACTS: ReadonlySet<string> = new Set<string>();

/**
 * Whether a fact can be read on a trigger. The ticket, organisation and contact facts exist on the ticket moments only; the
 * event facts on the moments whose payload has them. A field that is not a Service Desk fact is always available here.
 * Without a trigger (nothing is known yet) every fact is offered.
 */
export function serviceFactAvailable(field: string, trigger: WorkflowTrigger | undefined): boolean {
  if (trigger === undefined) return true;
  const namespace = field.split(".")[0] ?? field;
  if (SERVICE_NAMESPACES.has(namespace)) return isTicketTrigger(trigger);
  if (LATER_EVENT_FACTS.has(field)) return false;
  const wanted = EVENT_FACT_TRIGGERS[field];
  if (wanted !== undefined) return wanted.includes(trigger);
  return !isTicketTrigger(trigger) || TICKET_EVENT_FACTS[field]?.includes(trigger) !== false;
}

const values = (list: readonly string[]): readonly string[] => list;

/** Facts with a short list of values: the editor offers them as choices (labels: `conditions.choices.<fact>.<value>`). */
export const SERVICE_FIELD_CHOICES: Readonly<Record<string, readonly string[]>> = {
  "ticket.priority": values(TICKET_PRIORITY_VALUES),
  "ticket.sentiment": values(TICKET_SENTIMENT_VALUES),
  "ticket.status_category": values(STATUS_CATEGORY_VALUES),
  "ticket.category": values(TYPE_CATEGORY_VALUES),
  "ticket.channel": values(TICKET_CHANNEL_VALUES),
  "ticket.origin": values(TICKET_ORIGIN_VALUES),
  "ticket.sla.first_response_state": values(SLA_STATE_VALUES),
  "ticket.sla.resolution_state": values(SLA_STATE_VALUES),
  "contact.org_role": values(ORG_ROLE_VALUES),
  "contact.verified_via": values(VERIFIED_VIA_VALUES),
  "event.metric": values(EVENT_METRICS),
};

/** What a condition on a custom ticket field compares, from the field's type. */
export function ticketFieldKind(field: string, custom: readonly ServiceFieldOption[]): ConditionFieldKind | undefined {
  if (!Object.values(FACT_PREFIX).some((prefix) => field.startsWith(prefix))) return undefined;
  const type = custom.find((item) => factNameOf(item) === field)?.kind;
  if (type === "number" || type === "integer") return "number";
  if (type === "bool") return "bool";
  return type === "multi_select" ? "list" : "text";
}
